// SWR-style cache backed by sessionStorage + an in-memory L1.
// sessionStorage is preferred over localStorage because OS payloads contain
// PII (client names, addresses, suppliers) and should not persist across
// browser sessions in a banking context.
const STORAGE_PREFIX = 'dashboard:cache:';
const INDEX_KEY = 'dashboard:cache:index';
const MAX_ENTRIES = 20;
const STALE_TTL_MS = 5 * 60 * 1000; // 5 min — after this, refetch but still display cached data
const HARD_TTL_MS = 60 * 60 * 1000; // 1 h — after this, evict entirely

interface CacheEntry<T> {
  data: T;
  ts: number;
}

const memory = new Map<string, CacheEntry<unknown>>();

const storage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const readIndex = (): string[] => {
  const s = storage();
  if (!s) return [];
  try {
    const raw = s.getItem(INDEX_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
};

const writeIndex = (keys: string[]): void => {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(INDEX_KEY, JSON.stringify(keys));
  } catch {
    // storage full or unavailable, ignore
  }
};

const touchIndex = (key: string): void => {
  const s = storage();
  const idx = readIndex().filter((k) => k !== key);
  idx.unshift(key);
  while (idx.length > MAX_ENTRIES) {
    const evicted = idx.pop();
    if (evicted && s) {
      try {
        s.removeItem(STORAGE_PREFIX + evicted);
      } catch {
        // noop
      }
    }
  }
  writeIndex(idx);
};

const dropFromIndex = (key: string): void => {
  writeIndex(readIndex().filter((k) => k !== key));
};

export const readCache = <T>(key: string): CacheEntry<T> | null => {
  const mem = memory.get(key) as CacheEntry<T> | undefined;
  if (mem) {
    if (Date.now() - mem.ts > HARD_TTL_MS) {
      memory.delete(key);
      dropFromIndex(key);
      const s = storage();
      if (s) {
        try {
          s.removeItem(STORAGE_PREFIX + key);
        } catch {
          // noop
        }
      }
      return null;
    }
    return mem;
  }
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (Date.now() - parsed.ts > HARD_TTL_MS) {
      s.removeItem(STORAGE_PREFIX + key);
      dropFromIndex(key);
      return null;
    }
    memory.set(key, parsed as CacheEntry<unknown>);
    return parsed;
  } catch {
    return null;
  }
};

export const writeCache = <T>(key: string, data: T): void => {
  const entry: CacheEntry<T> = { data, ts: Date.now() };
  memory.set(key, entry as CacheEntry<unknown>);
  const s = storage();
  if (!s) return;
  try {
    s.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
    touchIndex(key);
  } catch {
    // Storage quota exceeded: drop the oldest entry and retry once.
    const idx = readIndex();
    if (idx.length > 0) {
      const oldest = idx[idx.length - 1];
      try {
        s.removeItem(STORAGE_PREFIX + oldest);
        writeIndex(idx.slice(0, -1));
        s.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
        touchIndex(key);
      } catch {
        // Still failing — give up on disk; in-memory cache remains.
      }
    }
  }
};

export const isStale = (ts: number): boolean => Date.now() - ts > STALE_TTL_MS;

// Inflight request deduplication: concurrent calls for the same key share a
// single Promise, so rapid filter changes don't fan out into multiple fetches.
const inflight = new Map<string, Promise<unknown>>();

export const dedupe = <T>(key: string, fn: () => Promise<T>): Promise<T> => {
  const existing = inflight.get(key) as Promise<T> | undefined;
  if (existing) return existing;
  const promise = fn().finally(() => {
    inflight.delete(key);
  });
  inflight.set(key, promise);
  return promise;
};
